import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import dizeeLogo from '../../assets/images/dizee logo.svg';

const DocsContainer = styled.div`
  min-height: 100vh;
  background: #000000;
  display: flex;
  flex-direction: column;
`;

const DocsHeader = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  z-index: 100;
  
  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const LogoImage = styled.img`
  width: 64px;
  height: auto;
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: color 0.2s ease;
  
  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const DocsMain = styled.main`
  display: flex;
  margin-top: 60px;
  min-height: calc(100vh - 60px);
`;

const Sidebar = styled.aside`
  width: 240px;
  background: #000000;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  padding: 24px 0;
  position: fixed;
  top: 60px;
  left: 0;
  bottom: 0;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
  
  @media (max-width: 1024px) {
    transform: translateX(${props => props.isOpen ? '0' : '-100%'});
    transition: transform 0.3s ease;
    z-index: 50;
    background: rgba(0, 0, 0, 0.98);
  }
`;

const SidebarSection = styled.div`
  margin-bottom: 24px;
  padding: 0 20px;
`;

const SectionTitle = styled.h3`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 8px 0;
  padding: 0 8px;
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavItem = styled.li`
  margin-bottom: 2px;
`;

const NavLink = styled.button`
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  border-radius: 4px;
  padding: 6px 8px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: ${props => props.active ? '#FF2300' : 'rgba(255, 255, 255, 0.6)'};
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.05);
  }
  
  ${props => props.active && `
    background: rgba(255, 35, 0, 0.08);
    color: #FF2300;
  `}
`;

const Content = styled.div`
  flex: 1;
  margin-left: 240px;
  padding: 40px 80px 80px;
  max-width: 900px;
  
  @media (max-width: 1024px) {
    margin-left: 0;
    padding: 32px 24px 60px;
  }
`;

const ArticleHeader = styled.div`
  margin-bottom: 32px;
`;

const ArticleTitle = styled.h1`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 32px;
  font-weight: 400;
  color: white;
  margin: 0 0 8px 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const ArticleDescription = styled.p`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.5;
  margin: 0;
`;

const ArticleSection = styled.section`
  margin-bottom: 40px;
`;

const SectionHeading = styled.h2`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: white;
  margin: 0 0 12px 0;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const SubsectionHeading = styled.h3`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.9);
  margin: 20px 0 10px 0;
  letter-spacing: 0;
  line-height: 1.4;
`;

const Paragraph = styled.p`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.65;
  margin: 0 0 12px 0;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0;
`;

const FeatureItem = styled.li`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.65;
  margin-bottom: 8px;
  padding-left: 18px;
  position: relative;
  
  &::before {
    content: '•';
    position: absolute;
    left: 6px;
    color: rgba(255, 255, 255, 0.3);
    font-size: 16px;
  }
`;

const CodeBlock = styled.pre`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 6px;
  padding: 16px;
  margin: 16px 0;
  overflow-x: auto;
  font-family: 'SF Mono', 'Monaco', 'Menlo', monospace;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.5;
`;

const InfoBox = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  padding: 16px;
  margin: 20px 0;
`;

const InfoBoxTitle = styled.h4`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 6px 0;
`;

const InfoBoxText = styled.p`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
  margin: 0;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 8px 12px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  margin-right: 16px;
  
  @media (max-width: 1024px) {
    display: block;
  }
`;

const DocsPage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('introduction');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    {
      title: 'Getting Started',
      items: [
        { id: 'introduction', label: 'Introduction' },
        { id: 'quick-start', label: 'Quick Start' },
        { id: 'platform-overview', label: 'Platform Overview' }
      ]
    },
    {
      title: 'Core Apps',
      items: [
        { id: 'catalog', label: 'Catalog' },
        { id: 'booking', label: 'Booking' },
        { id: 'analytics', label: 'Analytics' },
        { id: 'merch', label: 'Merch' },
        { id: 'email', label: 'Email' }
      ]
    },
    {
      title: 'Features',
      items: [
        { id: 'ai-assistant', label: 'AI Assistant' },
        { id: 'integrations', label: 'Integrations' },
        { id: 'automation', label: 'Automation' },
        { id: 'collaboration', label: 'Team Collaboration' }
      ]
    },
    {
      title: 'Account & Billing',
      items: [
        { id: 'pricing', label: 'Pricing & Credits' },
        { id: 'account', label: 'Account Management' },
        { id: 'security', label: 'Security & Privacy' }
      ]
    }
  ];

  const content = {
    introduction: {
      title: 'Introduction to DIZEE',
      description: 'Your AI-powered workspace for music business management',
      body: (
        <>
          <ArticleSection>
            <SectionHeading>What is DIZEE?</SectionHeading>
            <Paragraph>
              DIZEE is a comprehensive AI-powered music management platform designed to streamline every aspect of a music professional's career. From catalog management and booking coordination to merchandise tracking and email intelligence, DIZEE consolidates all music business operations into a unified, intelligent ecosystem.
            </Paragraph>
            <Paragraph>
              Built specifically for artists, managers, labels, and music industry professionals, DIZEE transforms how you manage your music career by providing intelligent insights, automated workflows, and comprehensive data management in one place.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Core Philosophy</SectionHeading>
            <FeatureList>
              <FeatureItem>AI-First Approach: Every feature is enhanced with intelligent automation and insights</FeatureItem>
              <FeatureItem>Unified Data: Seamless integration across all music business operations</FeatureItem>
              <FeatureItem>Weighted Usage System: Fair, usage-based resource allocation</FeatureItem>
              <FeatureItem>Professional Grade: Enterprise-ready tools for serious music professionals</FeatureItem>
            </FeatureList>
          </ArticleSection>

          <InfoBox>
            <InfoBoxTitle>New to DIZEE?</InfoBoxTitle>
            <InfoBoxText>
              Start with our Quick Start guide to set up your account and connect your first platforms in under 10 minutes.
            </InfoBoxText>
          </InfoBox>
        </>
      )
    },
    'quick-start': {
      title: 'Quick Start Guide',
      description: 'Get up and running with DIZEE in minutes',
      body: (
        <>
          <ArticleSection>
            <SectionHeading>Step 1: Create Your Account</SectionHeading>
            <Paragraph>
              Sign up for DIZEE using your email address or connect with your existing music platform accounts. Choose a plan that fits your needs—you can always upgrade later.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Step 2: Connect Your Platforms</SectionHeading>
            <Paragraph>
              Link your streaming services (Spotify, Apple Music, YouTube Music), social media accounts, email providers, and other music business tools. DIZEE will automatically begin syncing your data.
            </Paragraph>
            <FeatureList>
              <FeatureItem>Spotify for Artists</FeatureItem>
              <FeatureItem>Apple Music for Artists</FeatureItem>
              <FeatureItem>YouTube Analytics</FeatureItem>
              <FeatureItem>Instagram & TikTok</FeatureItem>
              <FeatureItem>Gmail or Outlook</FeatureItem>
            </FeatureList>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Step 3: Import Your Catalog</SectionHeading>
            <Paragraph>
              Upload your music catalog via CSV/Excel, or let DIZEE automatically discover your tracks from connected platforms. Our AI will consolidate duplicates and organize everything.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Step 4: Explore Your Apps</SectionHeading>
            <Paragraph>
              Navigate to your dashboard and explore the different apps available to you. Each app is designed to handle a specific aspect of your music business.
            </Paragraph>
          </ArticleSection>

          <InfoBox>
            <InfoBoxTitle>Setup Time</InfoBoxTitle>
            <InfoBoxText>
              Most users complete initial setup in under 10 minutes and see their first insights immediately.
            </InfoBoxText>
          </InfoBox>
        </>
      )
    },
    'platform-overview': {
      title: 'Platform Overview',
      description: 'Understanding the DIZEE ecosystem',
      body: (
        <>
          <ArticleSection>
            <SectionHeading>Multi-App Architecture</SectionHeading>
            <Paragraph>
              DIZEE is built around a modular app system where each app serves a specific function in your music business. All apps share the same underlying data, so information flows seamlessly between them.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Available Apps</SectionHeading>
            <SubsectionHeading>📊 Catalog</SubsectionHeading>
            <Paragraph>
              Your complete music catalog intelligence center. Track every release, manage metadata, monitor streaming performance, and get automated reminders for important deadlines.
            </Paragraph>

            <SubsectionHeading>📅 Booking</SubsectionHeading>
            <Paragraph>
              Intelligent show and event coordination. AI-powered email parsing automatically extracts booking details and creates structured events.
            </Paragraph>

            <SubsectionHeading>👕 Merch</SubsectionHeading>
            <Paragraph>
              Complete merchandise inventory and sales management. Track stock levels, monitor sales at shows, and get insights on what sells best in different markets.
            </Paragraph>

            <SubsectionHeading>📧 Email</SubsectionHeading>
            <Paragraph>
              AI-powered inbox management that understands music industry communication. Automatic categorization, smart filters, and intelligent notifications.
            </Paragraph>

            <SubsectionHeading>📈 Analytics</SubsectionHeading>
            <Paragraph>
              Comprehensive streaming and fan insights. Visualize your growth, understand your audience, and make data-driven decisions.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Weighted Usage System</SectionHeading>
            <Paragraph>
              DIZEE uses a fair, credit-based system where different actions consume different amounts of resources:
            </Paragraph>
            <FeatureList>
              <FeatureItem>Basic Actions (1 credit): Viewing data, simple searches, profile updates</FeatureItem>
              <FeatureItem>Enhanced Actions (3-5 credits): AI analysis, bulk imports, automated parsing</FeatureItem>
              <FeatureItem>Premium Actions (10+ credits): Advanced AI features, large-scale operations, predictive analytics</FeatureItem>
            </FeatureList>
          </ArticleSection>
        </>
      )
    },
    catalog: {
      title: 'Catalog Management',
      description: 'Your complete music catalog intelligence center',
      body: (
        <>
          <ArticleSection>
            <SectionHeading>Overview</SectionHeading>
            <Paragraph>
              The Catalog app is your central hub for managing your entire music library. It provides AI-powered discovery, comprehensive metadata management, and intelligent insights about your releases.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Key Features</SectionHeading>
            <FeatureList>
              <FeatureItem>AI-Powered Discovery: Automatic track data extraction from Spotify, Apple Music, and other platforms</FeatureItem>
              <FeatureItem>Bulk Import: CSV/Excel import with duplicate detection and data validation</FeatureItem>
              <FeatureItem>Release Workflow: Automated reminders for distributor deadlines, DSP submissions, and playlist pitching</FeatureItem>
              <FeatureItem>Analytics Integration: Track performance metrics and streaming data</FeatureItem>
              <FeatureItem>Metadata Management: Comprehensive track information with ISRC, UPC, and publishing data</FeatureItem>
            </FeatureList>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Adding Tracks</SectionHeading>
            <SubsectionHeading>Automatic Platform Sync</SubsectionHeading>
            <Paragraph>
              Connect your Spotify for Artists or Apple Music for Artists account. DIZEE will automatically discover all your releases, including singles, EPs, and albums. The system refreshes daily to catch new releases and updates.
            </Paragraph>
            
            <SubsectionHeading>CSV/Excel Import</SubsectionHeading>
            <Paragraph>
              Upload a spreadsheet with your catalog. Required fields include track name, artist, and release date. Optional fields: ISRC, UPC, label, genre, and streaming links.
            </Paragraph>
            
            <SubsectionHeading>Manual Entry</SubsectionHeading>
            <Paragraph>
              Add individual tracks through the web interface. Perfect for unreleased music or tracks not yet on streaming platforms.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Managing Metadata</SectionHeading>
            <Paragraph>
              Each track stores comprehensive information: release date, genres, mood tags, BPM, key, explicit content flags, copyright info, and more. Bulk edit multiple tracks at once to update labels, publishers, or rights holders efficiently.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Release Reminders</SectionHeading>
            <Paragraph>
              Set up your release calendar and get automated notifications for:
            </Paragraph>
            <FeatureList>
              <FeatureItem>Distributor upload deadlines (typically 2-4 weeks before release)</FeatureItem>
              <FeatureItem>Playlist pitching windows (4-7 days before release)</FeatureItem>
              <FeatureItem>Pre-save campaign launches</FeatureItem>
              <FeatureItem>Social media announcement schedules</FeatureItem>
            </FeatureList>
          </ArticleSection>

          <InfoBox>
            <InfoBoxTitle>Pro Tip</InfoBoxTitle>
            <InfoBoxText>
              Connect Spotify for Artists for automatic data syncing. DIZEE will continuously update your catalog with new releases and performance metrics.
            </InfoBoxText>
          </InfoBox>
        </>
      )
    },
    booking: {
      title: 'Booking Management',
      description: 'Intelligent show and event coordination',
      body: (
        <>
          <ArticleSection>
            <SectionHeading>Overview</SectionHeading>
            <Paragraph>
              The Booking app helps you manage your live performance calendar, from local gigs to international tours. AI-powered email parsing automatically extracts booking details and creates structured events.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Key Features</SectionHeading>
            <FeatureList>
              <FeatureItem>AI Email Parsing: Automatically extract booking details from emails</FeatureItem>
              <FeatureItem>Calendar Management: Visual timeline of all your shows and events</FeatureItem>
              <FeatureItem>Financial Tracking: Monitor deposits, guarantees, door splits, and expenses</FeatureItem>
              <FeatureItem>Rider Management: Store and share technical riders and hospitality requirements</FeatureItem>
              <FeatureItem>Travel Planning: Integrated routing and accommodation tracking</FeatureItem>
            </FeatureList>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Adding Shows</SectionHeading>
            <SubsectionHeading>Email Integration</SubsectionHeading>
            <Paragraph>
              Forward booking confirmations to DIZEE, and AI will extract venue name, date, time, payment terms, capacity, and contact information. Review and confirm before adding to your calendar.
            </Paragraph>
            
            <SubsectionHeading>Manual Entry</SubsectionHeading>
            <Paragraph>
              Create events directly in the app. Fill in basic details like date, venue, and city, then add financial terms, travel notes, and technical requirements as needed.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Tour Planning</SectionHeading>
            <Paragraph>
              Create tour folders to group related shows. DIZEE suggests optimal routing to minimize travel time and costs. Track total tour revenue, expenses, and profit margins in real-time.
            </Paragraph>
          </ArticleSection>

          <InfoBox>
            <InfoBoxTitle>Connect Gmail or Outlook</InfoBoxTitle>
            <InfoBoxText>
              Enable email integration to automatically capture booking details from venue communications.
            </InfoBoxText>
          </InfoBox>
        </>
      )
    },
    analytics: {
      title: 'Analytics Dashboard',
      description: 'Comprehensive streaming and fan insights',
      body: (
        <>
          <ArticleSection>
            <SectionHeading>Overview</SectionHeading>
            <Paragraph>
              The Analytics app provides real-time insights into your streaming performance, audience demographics, and revenue trends across all platforms.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Key Metrics</SectionHeading>
            <FeatureList>
              <FeatureItem>Streaming Data: Daily streams, monthly listeners, and playlist adds across Spotify, Apple Music, and YouTube</FeatureItem>
              <FeatureItem>Audience Insights: Listener demographics, geographic distribution, and listening patterns</FeatureItem>
              <FeatureItem>Revenue Tracking: Estimated earnings by platform, territory, and track</FeatureItem>
              <FeatureItem>Growth Metrics: Follower growth, save rates, and engagement trends</FeatureItem>
              <FeatureItem>Playlist Performance: Track editorial and algorithmic playlist placements</FeatureItem>
            </FeatureList>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Custom Dashboards</SectionHeading>
            <Paragraph>
              Create personalized dashboards focused on the metrics that matter most to you. Compare tracks, analyze release performance, or monitor specific markets. Export data as CSV for deeper analysis in spreadsheet tools.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>AI Insights</SectionHeading>
            <Paragraph>
              DIZEE's AI analyzes your data to surface actionable insights:
            </Paragraph>
            <FeatureList>
              <FeatureItem>Identify your fastest-growing markets for tour planning</FeatureItem>
              <FeatureItem>Detect unusual streaming patterns that may indicate playlist adds</FeatureItem>
              <FeatureItem>Predict release performance based on historical data</FeatureItem>
              <FeatureItem>Recommend optimal release timing based on your audience's listening habits</FeatureItem>
            </FeatureList>
          </ArticleSection>
        </>
      )
    },
    merch: {
      title: 'Merchandise Management',
      description: 'Complete inventory and sales tracking',
      body: (
        <>
          <ArticleSection>
            <SectionHeading>Overview</SectionHeading>
            <Paragraph>
              The Merch app helps you track inventory, monitor sales at shows, and understand what products resonate with your fans in different markets.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Key Features</SectionHeading>
            <FeatureList>
              <FeatureItem>Inventory Management: Track stock levels across multiple products and sizes</FeatureItem>
              <FeatureItem>Sales Tracking: Log sales at shows or through online stores</FeatureItem>
              <FeatureItem>Cost Analysis: Monitor production costs, profit margins, and revenue per show</FeatureItem>
              <FeatureItem>Low Stock Alerts: Get notified when items need reordering</FeatureItem>
              <FeatureItem>Market Insights: See what sells best in different cities and venues</FeatureItem>
            </FeatureList>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Adding Products</SectionHeading>
            <Paragraph>
              Create products with multiple variants (sizes, colors). Set costs, retail prices, and initial inventory levels. Track each variant separately to understand size preferences by market.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Tour Merch Planning</SectionHeading>
            <Paragraph>
              Before a tour, DIZEE analyzes historical data to recommend inventory quantities by city. During the tour, track nightly sales and adjust inventory allocations between venues.
            </Paragraph>
          </ArticleSection>

          <InfoBox>
            <InfoBoxTitle>Link to Shows</InfoBoxTitle>
            <InfoBoxText>
              Connect merch sales to booking events to see total show revenue (ticket + merch) in one place.
            </InfoBoxText>
          </InfoBox>
        </>
      )
    },
    email: {
      title: 'Email Management',
      description: 'AI-powered inbox intelligence',
      body: (
        <>
          <ArticleSection>
            <SectionHeading>Overview</SectionHeading>
            <Paragraph>
              The Email app brings AI-powered organization to your music business inbox. Automatically categorize messages, extract important details, and never miss a deadline or opportunity.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Key Features</SectionHeading>
            <FeatureList>
              <FeatureItem>Smart Categorization: Automatically sort messages into Bookings, Press, Collaborations, Business, and more</FeatureItem>
              <FeatureItem>Deadline Detection: AI identifies time-sensitive requests and creates reminders</FeatureItem>
              <FeatureItem>Opportunity Alerts: Get notified about playlist pitches, licensing requests, and collaboration offers</FeatureItem>
              <FeatureItem>Auto-extraction: Pull booking details, contract terms, and payment info from emails</FeatureItem>
              <FeatureItem>Priority Inbox: Important messages surface automatically</FeatureItem>
            </FeatureList>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Setting Up</SectionHeading>
            <Paragraph>
              Connect your Gmail or Outlook account. DIZEE analyzes your inbox history to learn your communication patterns and priorities. You control which folders or labels DIZEE monitors.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Privacy & Security</SectionHeading>
            <Paragraph>
              Your email content is processed securely and never stored permanently. DIZEE only retains extracted metadata (dates, sender info, categories) to improve suggestions. You can revoke access anytime.
            </Paragraph>
          </ArticleSection>
        </>
      )
    },
    'ai-assistant': {
      title: 'AI Assistant',
      description: 'Your 24/7 music business AI advisor',
      body: (
        <>
          <ArticleSection>
            <SectionHeading>Natural Language Queries</SectionHeading>
            <Paragraph>
              Ask questions about your data in plain English. DIZEE's AI understands context across all your apps and provides intelligent, actionable answers.
            </Paragraph>
            <CodeBlock>
              Examples:
              • "Which cities should I tour next?"
              • "How is my latest single performing?"
              • "Show me my merch sales from last month"
              • "When is my next distributor deadline?"
            </CodeBlock>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Cross-App Intelligence</SectionHeading>
            <Paragraph>
              The AI assistant has access to all your data across Catalog, Booking, Merch, Analytics, and Email. It can connect dots between different aspects of your business and provide holistic insights.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Proactive Insights</SectionHeading>
            <Paragraph>
              DIZEE doesn't just answer questions—it proactively alerts you to opportunities, potential issues, and important trends in your data.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>AI Capabilities</SectionHeading>
            <FeatureList>
              <FeatureItem>Email Analysis: Automatic extraction of booking details, contract terms, and opportunities</FeatureItem>
              <FeatureItem>Performance Prediction: AI forecasting for tour revenue and merchandise sales</FeatureItem>
              <FeatureItem>Market Intelligence: Competitive analysis and industry trend identification</FeatureItem>
              <FeatureItem>Content Generation: AI-assisted bio writing, press releases, and social media content</FeatureItem>
              <FeatureItem>Schedule Optimization: Intelligent tour routing and time management</FeatureItem>
            </FeatureList>
          </ArticleSection>
        </>
      )
    },
    pricing: {
      title: 'Pricing & Credits',
      description: 'Understanding DIZEE\'s usage-based pricing model',
      body: (
        <>
          <ArticleSection>
            <SectionHeading>Fair Resource Allocation</SectionHeading>
            <Paragraph>
              DIZEE employs a sophisticated weighted usage system that ensures fair access to AI and computational resources while providing enhanced capabilities for power users.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Usage Categories</SectionHeading>
            
            <SubsectionHeading>Basic Actions (1 point)</SubsectionHeading>
            <FeatureList>
              <FeatureItem>Simple data entry and viewing</FeatureItem>
              <FeatureItem>Basic search and filtering</FeatureItem>
              <FeatureItem>Standard CRUD operations</FeatureItem>
              <FeatureItem>Profile updates</FeatureItem>
            </FeatureList>

            <SubsectionHeading>Enhanced Actions (3-5 points)</SubsectionHeading>
            <FeatureList>
              <FeatureItem>AI-powered data analysis</FeatureItem>
              <FeatureItem>Bulk import operations</FeatureItem>
              <FeatureItem>Advanced search with AI</FeatureItem>
              <FeatureItem>Automated email parsing</FeatureItem>
            </FeatureList>

            <SubsectionHeading>Premium Actions (10+ points)</SubsectionHeading>
            <FeatureList>
              <FeatureItem>Predictive analytics and forecasting</FeatureItem>
              <FeatureItem>Large-scale data processing</FeatureItem>
              <FeatureItem>Complex AI content generation</FeatureItem>
              <FeatureItem>Real-time collaborative features</FeatureItem>
            </FeatureList>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Plans</SectionHeading>
            <Paragraph>
              Choose a plan that fits your needs. All plans include access to all apps—the difference is in credit allowances and advanced features.
            </Paragraph>
            <FeatureList>
              <FeatureItem>Starter (Free): Perfect for independent artists getting started</FeatureItem>
              <FeatureItem>Professional ($29/mo): For serious artists and small teams</FeatureItem>
              <FeatureItem>Enterprise (Custom): For labels and large organizations</FeatureItem>
            </FeatureList>
          </ArticleSection>

          <InfoBox>
            <InfoBoxTitle>Credit Usage</InfoBoxTitle>
            <InfoBoxText>
              Most users find the included credits in their plan sufficient for daily use. You can always purchase additional credits if needed.
            </InfoBoxText>
          </InfoBox>
        </>
      )
    },
    security: {
      title: 'Security & Privacy',
      description: 'How DIZEE protects your data',
      body: (
        <>
          <ArticleSection>
            <SectionHeading>Data Security</SectionHeading>
            <Paragraph>
              Your data is encrypted in transit (TLS/SSL) and at rest (AES-256). We use enterprise-grade security with regular audits and penetration testing.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Data Ownership</SectionHeading>
            <Paragraph>
              You own your data. Period. DIZEE never shares your data without explicit consent, and you can export all your data at any time in standard formats.
            </Paragraph>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Privacy Practices</SectionHeading>
            <FeatureList>
              <FeatureItem>We don't sell your data to third parties</FeatureItem>
              <FeatureItem>AI processing happens on secure servers, not third-party AI services</FeatureItem>
              <FeatureItem>Role-based access controls for team accounts</FeatureItem>
              <FeatureItem>Two-factor authentication available</FeatureItem>
              <FeatureItem>GDPR and CCPA compliant</FeatureItem>
            </FeatureList>
          </ArticleSection>

          <ArticleSection>
            <SectionHeading>Data Retention</SectionHeading>
            <Paragraph>
              Active accounts: Data retained indefinitely
            </Paragraph>
            <Paragraph>
              Canceled accounts: Read-only access for 90 days, then data archived for 1 year before permanent deletion (unless you request immediate deletion)
            </Paragraph>
          </ArticleSection>
        </>
      )
    }
  };

  const currentContent = content[activeSection] || content.introduction;

  return (
    <DocsContainer>
      <DocsHeader>
        <LeftSection>
          <MobileMenuButton onClick={() => setSidebarOpen(!sidebarOpen)}>
            ☰ Menu
          </MobileMenuButton>
          <LogoContainer onClick={() => navigate('/')}>
            <LogoImage src={dizeeLogo} alt="DIZEE" />
          </LogoContainer>
        </LeftSection>
        <BackButton onClick={() => navigate('/')}>
          Back
        </BackButton>
      </DocsHeader>

      <DocsMain>
        <Sidebar isOpen={sidebarOpen}>
          {navigation.map((section, idx) => (
            <SidebarSection key={idx}>
              <SectionTitle>{section.title}</SectionTitle>
              <NavList>
                {section.items.map((item) => (
                  <NavItem key={item.id}>
                    <NavLink
                      active={activeSection === item.id}
                      onClick={() => {
                        setActiveSection(item.id);
                        setSidebarOpen(false);
                      }}
                    >
                      {item.label}
                    </NavLink>
                  </NavItem>
                ))}
              </NavList>
            </SidebarSection>
          ))}
        </Sidebar>

        <Content>
          <ArticleHeader>
            <ArticleTitle>{currentContent.title}</ArticleTitle>
            <ArticleDescription>{currentContent.description}</ArticleDescription>
          </ArticleHeader>

          {currentContent.body}
        </Content>
      </DocsMain>
    </DocsContainer>
  );
};

export default DocsPage; 